import { FormControl, FormHelperText, MenuItem, Select } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { getPartPrice } from '../../../fetch';
import { IOrderDetailPart, IPartPrice } from '../../../models';

interface IVendorRow {
  part: IOrderDetailPart;
  handleVendorChange: (val: number, price: number) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  setValidationVendors?: Dispatch<SetStateAction<IPartPrice[]>>;
  showError?: boolean;
}

export const VendorRow: FC<IVendorRow> = ({ part, handleVendorChange, setValidationVendors, isDisabled = false, isRequired = false, showError = false }) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [vendors, setVendors] = useState<IPartPrice[]>([]);

  const fetchVendors = async () => {
    if (vendors.length === 0) {
      try {
        const res = await getPartPrice({
          // we have two different models for parts IPart and IOrderDetailPart so check for the id differently
          // used on place-order and order detail pages
          id: part?.partId ?? part?.id
        });
        setVendors(res.records);
        if (setValidationVendors) setValidationVendors(res.records);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchVendors();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const currentSelectedVendor = vendors.find(vendor => vendor.vendorId === Number(part.vendorId));
  const classes = useStyles();

  return (
    <FormControl fullWidth variant="outlined" size="small" error={!part.vendorId}>
      <Select
        disabled={isLoading || isDisabled}
        name="vendor"
        labelId="vendor"
        id="vendor"
        required={isRequired}
        value={part.vendorId || ''}
        error={!part.vendorId || (showError && !currentSelectedVendor?.hasPoManager)}
        classes={{
          outlined: !currentSelectedVendor?.hasPoManager ? classes.optionInactive : undefined
        }}
        onChange={e => {
          const selectedVendor = vendors.find(vendor => vendor.vendorId === Number(e.target.value));
          handleVendorChange(selectedVendor.vendorId, selectedVendor.price);
        }}
      >
        {vendors &&
          vendors.map((vendor, index) => {
            return (
              <MenuItem key={`${index}`} disabled={!vendor.hasPoManager} value={vendor.vendorId}>
                {vendor.vendorName} {!vendor.hasPoManager && '(no PO Manager email)'}
              </MenuItem>
            );
          })}
      </Select>
      {!part.vendorId && isRequired && <FormHelperText sx={{ lineHeight: 'initial', marginTop: '-30px', fontSize: '0.85rem', marginBottom: 'revert' }}>Required</FormHelperText>}
      {showError && !currentSelectedVendor?.hasPoManager && !!part.vendorId && !isLoading && (
        <FormHelperText className={classes.vendorNoPO}>Setup a PO manager for this vendor or choose another vendor</FormHelperText>
      )}
    </FormControl>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  optionInactive: {
    opacity: 0.38
  },
  vendorNoPO: {
    color: theme.palette.error.main,
    margin: 0,
    position: 'absolute',
    top: 41
  }
}));
